import { Chess, castlingSide } from 'chessops/chess';
import { makeFen, parseFen } from 'chessops/fen';
import { makeSan } from 'chessops/san';
import type { Move, Role } from 'chessops/types';
import { kingCastlesTo, makeUci, parseUci, squareRank } from 'chessops/util';
import type { UciMove } from '../api/types';

/**
 * Chess rules used only by the in-browser §4 mock server. Product UI and the
 * Chessground adapter still consume server-supplied legal moves and never make
 * legality or canonical-identity decisions themselves.
 */

const PROMOTION_ROLES: Role[] = ['queen', 'rook', 'bishop', 'knight'];

function position(fen: string): Chess {
  return Chess.fromSetup(parseFen(fen).unwrap()).unwrap();
}

function apiMove(pos: Chess, move: Move): Move {
  if ('from' in move) {
    const side = castlingSide(pos, move);
    if (side) return { from: move.from, to: kingCastlesTo(pos.turn, side) };
  }
  return move;
}

/** Legal UCI moves exactly as a mock `RepertoireNode.legalMoves` response. */
export function legalUciMoves(fen: string): UciMove[] {
  const pos = position(fen);
  const moves: UciMove[] = [];
  const ctx = pos.ctx();
  for (const [from, destinations] of pos.allDests(ctx)) {
    const promotes =
      pos.board.pawn.has(from) && squareRank(from) === (pos.turn === 'white' ? 6 : 1);
    const roles: Array<Role | undefined> = promotes ? PROMOTION_ROLES : [undefined];
    for (const to of destinations) {
      for (const promotion of roles) {
        moves.push(makeUci(apiMove(pos, { from, to, promotion })) as UciMove);
      }
    }
  }
  return moves.sort();
}

/** Validate and play one API UCI move, returning the mock server's next state. */
export function playUci(fen: string, uci: string): { fen: string; san: string } | null {
  const pos = position(fen);
  const move = parseUci(uci);
  if (!move || !pos.isLegal(move)) return null;
  const san = makeSan(pos, move);
  pos.play(move);
  return { fen: makeFen(pos.toSetup()), san };
}
